import { useEffect, useState } from "react";
import { Anchor, Search, ChevronLeft, ChevronRight, AlertCircle, FileText, FileSpreadsheet, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { API, getStaffToken } from "../../lib/api";

const DIRECTION_FR = { aller: "Aller", retour: "Retour" };

function formatDateTimeFR(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

function formatDateFR(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

export default function StaffCheckinsHistory() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [date, setDate] = useState("");
  const [boatTime, setBoatTime] = useState("");
  const [direction, setDirection] = useState("");
  const [offerType, setOfferType] = useState("");
  const [offersCatalog, setOffersCatalog] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    // Load offer catalog once to populate the ticket-type filter dropdown
    api.get("/offers").then((r) => setOffersCatalog(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: "50" });
    if (date) params.append("date", date);
    if (boatTime) params.append("boat_time", boatTime);
    if (direction) params.append("direction", direction);
    if (offerType) params.append("offer_type", offerType);
    if (search) params.append("q", search);
    api.get(`/staff/checkins/history?${params}`)
      .then((r) => {
        setItems(r.data.items || []);
        setSummary(r.data.summary || []);
        setTotal(r.data.total || 0);
        setPages(r.data.pages || 1);
        setError(null);
      })
      .catch((err) => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || err.message || "Erreur inconnue";
        const msg = status
          ? `Erreur ${status} sur /staff/checkins/history — ${detail}`
          : `Impossible de joindre le serveur (${detail})`;
        setError(msg);
        setItems([]);
        setSummary([]);
        setTotal(0);
        setPages(1);
        toast.error(msg, { duration: 6000 });
      })
      .finally(() => setLoading(false));
  }, [page, date, boatTime, direction, offerType, search]);

  const reset = () => {
    setDate("");
    setBoatTime("");
    setDirection("");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="staff-checkins-history">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display-serif text-3xl md:text-4xl text-[#0A0A0A] inline-flex items-center gap-3">
            <Anchor size={26} className="text-[#B8922A]" /> Historique embarquements
          </h1>
          <p className="text-sm text-[#0A0A0A]/55 mt-1">
            Traçabilité de chaque scan ticket par bateau, par date et par heure.
          </p>
        </div>
        {/* iter-103 · Manifest exports for the CURRENT filter (date/boat/direction/offer/search). */}
        <ExportManifestToolbar
          date={date} boatTime={boatTime} direction={direction}
          offerType={offerType} q={search} count={total}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="mb-4 p-3 bg-red-50 border border-red-300 text-[0.78rem] text-red-700 flex items-start gap-2"
          data-testid="checkins-error"
        >
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <strong>L'API n'a pas pu retourner les embarquements.</strong> {error}
            <br />
            <span className="text-red-600/80">
              Si vous êtes en production, vérifiez que la dernière version a été redéployée
              (la route a été ajoutée au refactor d'iter-25). Sinon, contactez l'équipe technique.
            </span>
          </div>
        </div>
      )}

      {/* Summary by boat (current filter) */}
      {summary.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-6" data-testid="checkins-summary">
          {summary.slice(0, 12).map((s, i) => (
            <div key={i} className="bg-white border border-[#0A0A0A]/8 p-3">
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 truncate">{s.boat_label}</div>
              <div className="font-display-serif text-xl text-[#0A0A0A] mt-0.5">{s.count}</div>
              {(s.half_fare > 0 || s.free_children > 0) && (
                <div className="text-[0.6rem] text-[#0A0A0A]/55 mt-0.5">
                  {s.adults}A
                  {s.half_fare > 0 && <> · <span className="text-amber-700">{s.half_fare}D</span></>}
                  {s.free_children > 0 && <> · <span className="text-sky-700">{s.free_children}G</span></>}
                </div>
              )}
              <div className="text-[0.65rem] text-[#0A0A0A]/45">{formatDateFR(s.boat_date)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[#0A0A0A]/8 p-4 sm:p-5 mb-6" data-testid="checkins-filters">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <label className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 mb-1.5 block">Recherche</label>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A0A0A]/40" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setSearch(searchInput); setPage(1); } }}
                placeholder="Nom du client, du staff ou du skipper…"
                className="w-full pl-9 pr-3 py-2 border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none text-sm bg-white"
                data-testid="checkins-search"
              />
            </div>
          </div>
          <div>
            <label className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 mb-1.5 block">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none text-sm bg-white"
              data-testid="checkins-date"
            />
          </div>
          <div>
            <label className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 mb-1.5 block">Bateau</label>
            <input
              type="text"
              value={boatTime}
              onChange={(e) => { setBoatTime(e.target.value); setPage(1); }}
              placeholder="ex. 10H"
              className="w-24 px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none text-sm bg-white"
              data-testid="checkins-boat"
            />
          </div>
          <button onClick={reset} className="px-4 py-2 text-[0.65rem] uppercase tracking-[0.22em] border border-[#0A0A0A]/15 text-[#0A0A0A]/65 hover:border-[#0A0A0A] hover:text-[#0A0A0A] transition-colors" data-testid="checkins-reset">
            Réinitialiser
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="checkins-direction-tabs">
          {[["", "Tous"], ["aller", "Aller"], ["retour", "Retour"]].map(([id, label]) => (
            <button
              key={id || "all"}
              onClick={() => { setDirection(id); setPage(1); }}
              className={`px-3 py-1.5 text-[0.62rem] uppercase tracking-[0.22em] border transition-all ${
                direction === id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:border-[#B8922A]"
              }`}
              data-testid={`checkins-direction-${id || "all"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="mt-3" data-testid="checkins-offer-filter-row">
          <label className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0A0A0A]/55 block mb-1">Type de ticket</label>
          <select
            value={offerType}
            onChange={(e) => { setOfferType(e.target.value); setPage(1); }}
            className="border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none min-w-[14rem]"
            data-testid="checkins-offer-type-select"
          >
            <option value="">Tous les tickets</option>
            <option value="special_event">Événements spéciaux</option>
            {offersCatalog.map((o) => (
              <option key={o.id} value={o.id}>{o.name_fr}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#0A0A0A]/8 overflow-x-auto" data-testid="checkins-table">
        <table className="w-full text-sm min-w-[960px]">
          <thead>
            <tr className="text-left text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 border-b border-[#0A0A0A]/10 bg-[#FAFAF7]">
              <th className="py-3 px-4">Date / Heure</th>
              <th className="py-3 px-4">Client</th>
              <th className="py-3 px-4">Sens</th>
              <th className="py-3 px-4">Bateau</th>
              <th className="py-3 px-4">Offre</th>
              <th className="py-3 px-4">Staff</th>
              <th className="py-3 px-4">Skipper</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-10 text-center text-[#0A0A0A]/50">Chargement…</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-10 text-center text-[#0A0A0A]/50">
                  {date || boatTime || direction || offerType || search ? (
                    <>Aucun embarquement pour ces filtres.</>
                  ) : (
                    <>
                      <div className="mb-1">Aucun embarquement scanné pour l'instant.</div>
                      <div className="text-[0.72rem] text-[#0A0A0A]/40">
                        Les scans apparaissent ici dès qu'un staff valide un ticket sur le scanner QR.
                      </div>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              items.map((s, i) => (
                <tr key={`${s.booking_id}-${s.qr_token}-${i}`} className={`border-b border-[#0A0A0A]/5 hover:bg-[#FAFAF7]/50 ${s.passenger_kind && s.passenger_kind !== "adult" ? "bg-[#FAF7F2]/40" : ""}`} data-testid={`checkin-row-${i}`}>
                  <td className="py-2.5 px-4 text-[0.78rem] text-[#0A0A0A]">{formatDateTimeFR(s.scanned_at)}</td>
                  <td className="py-2.5 px-4">
                    {s.passenger_kind === "half_fare" || s.passenger_kind === "free" ? (
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 text-[0.55rem] uppercase tracking-[0.15em] ${s.passenger_kind === "half_fare" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>
                          {s.passenger_kind === "half_fare" ? "Demi-tarif" : "Gratuit <6"}
                        </span>
                        <span className="text-[0.85rem] text-[#0A0A0A]/70">rattaché à {s.attached_to}</span>
                      </div>
                    ) : (
                      <>
                        <div className="font-medium text-[#0A0A0A]">{s.guest_surname} {s.guest_name}</div>
                        {(s.guest_phone || s.guest_email) && (
                          <div className="text-[0.68rem] text-[#0A0A0A]/55">{s.guest_phone || s.guest_email}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="py-2.5 px-4">
                    <span className={`inline-block px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.18em] border rounded-sm ${s.direction === "aller" ? "text-green-700 border-green-300 bg-green-50" : "text-blue-700 border-blue-300 bg-blue-50"}`}>
                      {DIRECTION_FR[s.direction] || s.direction}
                    </span>
                  </td>
                  <td className="py-2.5 px-4">
                    <div className="font-medium text-[#0A0A0A]">{s.boat_time || "—"}</div>
                    {s.boat_name && (
                      <div className="text-[0.68rem] text-[#B8922A]">{s.boat_name}</div>
                    )}
                    <div className="text-[0.68rem] text-[#0A0A0A]/55">{formatDateFR(s.boat_date)}</div>
                    {s.overridden && (
                      <span className="inline-block mt-0.5 text-[0.58rem] uppercase tracking-[0.18em] text-amber-700 border border-amber-300 bg-amber-50 px-1.5 py-0.5 rounded-sm" title={`Prévu : ${s.planned_boat_time || "—"}`}>
                        Modifié
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-[0.78rem] text-[#0A0A0A]/75">{s.offer_name || "—"}</td>
                  <td className="py-2.5 px-4 text-[0.78rem] text-[#0A0A0A]/75">
                    <div>{s.staff_name || s.staff_email?.split("@")[0] || "—"}</div>
                    {s.staff_email && <div className="text-[0.62rem] text-[#0A0A0A]/45">{s.staff_email}</div>}
                  </td>
                  <td className="py-2.5 px-4 text-[0.78rem] text-[#0A0A0A]/75" data-testid={`checkin-skipper-${i}`}>
                    {s.skipper_name || <span className="text-[#0A0A0A]/35">—</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <div className="text-[0.72rem] text-[#0A0A0A]/55">Page {page} / {pages} · {total} embarquements</div>
          <div className="flex gap-1.5">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="p-2 border border-[#0A0A0A]/15 hover:border-[#B8922A] disabled:opacity-30" data-testid="checkins-prev"><ChevronLeft size={13} /></button>
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="p-2 border border-[#0A0A0A]/15 hover:border-[#B8922A] disabled:opacity-30" data-testid="checkins-next"><ChevronRight size={13} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// iter-103 · Export the current filtered manifest as CSV / XLSX / PDF.
// Bug reported by the user: « On arrive toujours pas à exporter la liste
// des passagers d'un bateau après une traversée ». Previously the exports
// only existed on the active Embarquement page — once the crossing was
// terminée and only visible in Historique, no export was possible.
// ═══════════════════════════════════════════════════════════════════════
function ExportManifestToolbar({ date, boatTime, direction, offerType, q, count }) {
  const [busy, setBusy] = useState(null);
  const download = async (format, mime) => {
    setBusy(format);
    try {
      const token = getStaffToken();
      const params = new URLSearchParams();
      if (date) params.set("date", date);
      if (boatTime) params.set("boat_time", boatTime);
      if (direction) params.set("direction", direction);
      if (offerType) params.set("offer_type", offerType);
      if (q) params.set("q", q);
      const url = `${API}/api/staff/checkins/history/export.${format}?${params}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        toast.error(text?.slice(0, 140) || `Export ${format.toUpperCase()} impossible`);
        return;
      }
      const blob = new Blob([await res.blob()], { type: mime });
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      const fname = ["embarquements", date || "export", boatTime].filter(Boolean).join("-");
      a.download = `${fname}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success(`Manifeste ${format.toUpperCase()} téléchargé (${count} lignes)`);
    } catch (e) {
      toast.error(e?.message || `Export ${format.toUpperCase()} impossible`);
    } finally {
      setBusy(null);
    }
  };

  const disabled = busy != null;
  return (
    <div className="flex items-center gap-2" data-testid="export-manifest-toolbar">
      <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/50 mr-1 hidden sm:block">
        Exporter
      </div>
      <button
        onClick={() => download("csv", "text/csv")}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-[0.65rem] uppercase tracking-[0.18em] border border-[#0A0A0A]/15 hover:border-[#B8922A] hover:text-[#B8922A] transition-colors disabled:opacity-40"
        data-testid="export-manifest-csv"
        title="Exporter le manifeste filtré en CSV"
      >
        <FileText size={12} /> {busy === "csv" ? "…" : "CSV"}
      </button>
      <button
        onClick={() => download("xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-[0.65rem] uppercase tracking-[0.18em] border border-[#0A0A0A]/15 hover:border-[#B8922A] hover:text-[#B8922A] transition-colors disabled:opacity-40"
        data-testid="export-manifest-xlsx"
        title="Exporter le manifeste filtré en Excel"
      >
        <FileSpreadsheet size={12} /> {busy === "xlsx" ? "…" : "Excel"}
      </button>
      <button
        onClick={() => download("pdf", "application/pdf")}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 px-3 py-2 text-[0.65rem] uppercase tracking-[0.18em] bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white transition-colors disabled:opacity-40"
        data-testid="export-manifest-pdf"
        title="Exporter le manifeste filtré en PDF"
      >
        <FileDown size={12} /> {busy === "pdf" ? "…" : "PDF"}
      </button>
    </div>
  );
}

